import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";

const GOLD = "#C9A84C";
// Glow intensity by seniority tier (4 = Chairman / Co-Founder … 1 = Observer / Alumni)
const TIER = {
  4: { glow: `0 0 10px ${GOLD}, 0 0 26px ${GOLD}aa, 0 0 54px ${GOLD}66`, bg: `${GOLD}2e`, border: GOLD, text: "#FFF3D1", pulse: 2.6 },
  3: { glow: `0 0 8px ${GOLD}cc, 0 0 20px ${GOLD}77`, bg: `${GOLD}22`, border: `${GOLD}dd`, text: "#F8E9BF", pulse: 3.2 },
  2: { glow: `0 0 6px ${GOLD}88, 0 0 12px ${GOLD}44`, bg: `${GOLD}16`, border: `${GOLD}99`, text: "#EBD9A8", pulse: 4 },
  1: { glow: `0 0 4px ${GOLD}55`, bg: `${GOLD}0d`, border: `${GOLD}55`, text: "#CDBE93", pulse: 5 },
};

export function RoleBadge({ role, size = "md", pulse = false }) {
  if (!role) return null;
  const t = TIER[role.tier] || TIER[1];
  const pad = size === "lg" ? "px-5 py-2 text-[11px]" : size === "sm" ? "px-2.5 py-0.5 text-[8px]" : "px-3.5 py-1 text-[9px]";
  return (
    <motion.span data-testid="director-role-badge" data-role={role.key} data-tier={role.tier} title={`${role.label} · ${role.group_label}`}
      className={`inline-flex items-center gap-2 border font-grotesk uppercase tracking-[0.32em] rounded-full ${pad}`}
      style={{ color: t.text, borderColor: t.border, background: t.bg, boxShadow: t.glow, textShadow: role.tier >= 3 ? `0 0 10px ${GOLD}99` : "none" }}
      animate={pulse ? { boxShadow: [t.glow, `0 0 18px ${GOLD}, 0 0 48px ${GOLD}cc, 0 0 90px ${GOLD}77`, t.glow] } : { boxShadow: t.glow }}
      transition={pulse ? { duration: t.pulse, repeat: Infinity, ease: "easeInOut" } : { duration: 0.6 }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: GOLD, boxShadow: `0 0 8px ${GOLD}` }} />
      {role.label}
    </motion.span>
  );
}

const pulseGateOpen = () => !!document.querySelector('[data-testid="security-pulse-modal"], [data-testid="pulse-modal"], [data-testid="security-pulse-gate"]');

// Subtle gold pulse that washes over the director dashboard once after a role change (waits for the Security Pulse gate to close).
export function RolePulse({ role, onDone }) {
  // Grace period: the Security Pulse gate opens ~1s after mount, so wait before the first check.
  const [held, setHeld] = useState(true);
  const [show, setShow] = useState(true);
  const [started, setStarted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setStarted(true), 1800); return () => clearTimeout(t); }, []);
  useEffect(() => { if (!started) return undefined; setHeld(pulseGateOpen()); const t = setInterval(() => setHeld(pulseGateOpen()), 400); return () => clearInterval(t); }, [started]);
  useEffect(() => { if (held || !show) return undefined; const t = setTimeout(() => setShow(false), 5200); return () => clearTimeout(t); }, [held, show]);
  const finished = useRef(false);
  const done = () => { if (finished.current) return; finished.current = true; onDone?.(); };
  if (!role || held) return null;
  return createPortal(
    <AnimatePresence onExitComplete={done}>
      {show && (
        <motion.div data-testid="role-pulse-overlay" className="fixed inset-0 z-[290] pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 1.4 } }}>
          {[0, 0.9, 1.8].map((d) => (
            <motion.div key={d} className="absolute left-1/2 top-1/2 rounded-full border" style={{ borderColor: `${GOLD}aa`, width: 120, height: 120, marginLeft: -60, marginTop: -60, boxShadow: `0 0 40px ${GOLD}66 inset, 0 0 60px ${GOLD}44` }}
              initial={{ scale: 0.2, opacity: 0 }} animate={{ scale: [0.2, 9, 14], opacity: [0, 0.55, 0] }} transition={{ duration: 3.6, delay: d, ease: "easeOut" }} />
          ))}
          <motion.div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 50%, ${GOLD}26, transparent 55%)` }}
            initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 0] }} transition={{ duration: 4.2, ease: "easeInOut" }} />
          <motion.div className="absolute left-1/2 top-[18%] -translate-x-1/2 text-center" initial={{ opacity: 0, y: 14 }} animate={{ opacity: [0, 1, 1, 0], y: [14, 0, 0, -8] }} transition={{ duration: 4.6, times: [0, 0.2, 0.8, 1] }}>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.5em]" style={{ color: `${GOLD}cc` }}>Board designation updated</div>
            <div className="mt-3 font-serif text-3xl" style={{ color: "#F5F5F0", textShadow: `0 0 24px ${GOLD}99` }}>{role.label}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

export function RoleSelect({ api, directorId, value, onChanged }) {
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/director-roles/catalogue").then((r) => setGroups(r.data.groups)).catch(() => {}); }, [api]);
  const change = async (e) => {
    const role = e.target.value || null;
    setBusy(true);
    try { const r = await api.put(`/admin/directors/${directorId}/role`, { role }); onChanged?.(r.data); }
    catch { /* toast handled by caller via onChanged absence */ }
    finally { setBusy(false); }
  };
  return (
    <label className="inline-flex items-center gap-2">
      <span className="font-grotesk text-[8px] uppercase tracking-[0.24em] text-[#8A8A82]">Board role</span>
      <select data-testid={`director-role-select-${directorId}`} value={value || ""} onChange={change} disabled={busy}
              className="bg-black/50 border border-[#C9A84C]/40 text-[#F5F5F0] text-[11px] px-2 py-1 rounded-sm focus:outline-none focus:border-[#C9A84C]">
        <option value="">— No designation —</option>
        {groups.map((g) => (
          <optgroup key={g.key} label={g.label}>
            {g.roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </optgroup>
        ))}
      </select>
    </label>
  );
}
